import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useMenuStore } from "@/stores/menu";
import { ALL_KIND_MENUS, kindOf } from "@/utils/invoiceKinds";
import { nativeRoute } from "@/config/menu";

const routes: RouteRecordRaw[] = [
    {
        path: "/",
        // ?go=<action key> comes from the V1 switch button: the V2 pair of the screen that was open there.
        redirect: (to) => nativeRoute(String(to.query.go ?? "")) ?? "/financial-data",
    },
    {
        path: "/financial-data",
        name: "financial-data",
        component: () => import("@/views/FinancialDataView.vue"),
        meta: { title: "Finančni podatki" },
    },
    {
        path: "/dashboard",
        name: "dashboard",
        component: () => import("@/views/DashboardView.vue"),
        meta: { title: "Pregled" },
    },
    {
        path: "/invoices",
        name: "invoices",
        component: () => import("@/views/InvoicesView.vue"),
        meta: { title: "Računi" },
    },
    {
        // The list was shown for a while at /invoices22; links to it still land on the list.
        path: "/invoices22",
        redirect: (to) => ({ name: "invoices", query: to.query }),
    },
    {
        // The invoice number goes in the query string, not the path: cash invoice numbers
        // ("PIS-BLA1-2 / 19") contain a slash, and Apache refuses an encoded slash in a path.
        path: "/invoice",
        name: "invoice-detail",
        component: () => import("@/views/InvoiceDetailView.vue"),
        props: (route) => ({
            id: String(route.query.id ?? ""),
            kind: kindOf(route.query.kind),
            from: String(route.query.from ?? ""),
        }),
        meta: { title: "Račun" },
    },
    {
        path: "/invoice/edit",
        name: "invoice-edit",
        component: () => import("@/views/InvoiceEditView.vue"),
        props: (route) => ({ id: String(route.query.id ?? ""), kind: ["prejeti", "masovni", "gotovinski", "ponudbe"].includes(String(route.query.kind)) ? String(route.query.kind) : "racuni" }),
        meta: { title: "Urejanje računa" },
    },
    {
        // A new issued invoice or offer; copyId makes the invoice from that offer.
        path: "/invoice/new",
        name: "invoice-new",
        component: () => import("@/views/InvoiceEditView.vue"),
        props: (route) => ({ id: "", from: String(route.query.from ?? ""), copyId: String(route.query.copyId ?? ""), copyKind: String(route.query.copyKind ?? ""), type: ["avans", "dobropis"].includes(String(route.query.type)) ? String(route.query.type) : "", kind: ["ponudbe", "prejeti", "gotovinski"].includes(String(route.query.kind)) ? String(route.query.kind) : "racuni" }),
        meta: { title: "Nov dokument" },
    },
    {
        // Old numeric links keep working.
        path: "/invoices/:id(\\d+)",
        redirect: (to) => ({ name: "invoice-detail", query: { id: String(to.params.id) } }),
    },
    {
        // Classic (V1) component hosted in an iframe inside the V2 shell.
        path: "/legacy/:component",
        name: "legacy",
        component: () => import("@/views/EmbedView.vue"),
        props: true,
        meta: { title: "Klasični zaslon" },
    },
    {
        path: "/bilance/pregled",
        name: "bilance-pregled",
        component: () => import("@/views/BilanceView.vue"),
        meta: { title: "Bilance" },
    },
    {
        path: "/bilance/uvoz",
        name: "bilance-uvoz",
        component: () => import("@/views/BilanceUvozView.vue"),
        meta: { title: "Uvoz bilance" },
    },
    {
        // The Stripe subscription of a "Sam svoj računovodja" company, with the one-click cancellation.
        path: "/narocnina",
        name: "subscription",
        component: () => import("@/views/SubscriptionView.vue"),
        meta: { title: "Naročnina" },
    },
    {
        // What was entered at the registration (the three setup steps), shown for reading.
        path: "/nastavitve",
        name: "settings",
        component: () => import("@/views/SettingsView.vue"),
        meta: { title: "Nastavitve" },
    },
    {
        path: "/admin",
        name: "admin",
        component: () => import("@/views/AdminHubView.vue"),
        meta: { title: "Admin" },
    },
    {
        // mojFreelancer agency CRM (staff only): three-pane companies / contacts / follow-ups.
        path: "/crm/:id(\d+)?",
        name: "crm",
        component: () => import("@/views/CrmView.vue"),
        meta: { title: "CRM" },
    },
    {
        // Šifrant partnerjev of a "Sam svoj računovodja" company (the classic screen stays for every other company).
        path: "/partners",
        name: "partners",
        component: () => import("@/views/PartnersView.vue"),
        meta: { title: "Partnerji" },
    },
    {
        // The id goes in the query string (empty = new partner), like the invoice number.
        path: "/partner",
        name: "partner-edit",
        component: () => import("@/views/PartnerEditView.vue"),
        meta: { title: "Partner" },
    },
    {
        // Contact persons of all partners (the classic partnerji_kontaktneOsebe).
        path: "/partner-contacts",
        name: "partner-contacts",
        component: () => import("@/views/PartnerContactsView.vue"),
        meta: { title: "Kontaktne osebe" },
    },
    {
        // Subscribers to e-invoices (the classic partnerji_eRacuniNarocniki).
        path: "/einvoice-subscribers",
        name: "einvoice-subscribers",
        component: () => import("@/views/EInvoiceSubscribersView.vue"),
        meta: { title: "Naročniki e-računov" },
    },
    {
        // Šifranti of articles: groups, articles, invoice clauses, post offices.
        path: "/item-groups",
        name: "item-groups",
        component: () => import("@/views/ItemGroupsView.vue"),
        meta: { title: "Skupine artiklov" },
    },
    {
        path: "/articles",
        name: "articles",
        component: () => import("@/views/ArticlesView.vue"),
        meta: { title: "Material, blago, storitve" },
    },
    {
        path: "/invoice-look",
        name: "invoice-look",
        component: () => import("@/views/InvoiceLookView.vue"),
        meta: { title: "Izgled računa" },
    },
    {
        path: "/clauses",
        name: "clauses",
        component: () => import("@/views/ClausesView.vue"),
        meta: { title: "Klavzule" },
    },
    {
        path: "/postal-codes",
        name: "postal-codes",
        component: () => import("@/views/PostalCodesView.vue"),
        meta: { title: "Pošte" },
    },
    {
        // Travel-order šifranti (the classic sifranti_vozila and sifranti_kilometrine).
        path: "/vehicles",
        name: "vehicles",
        component: () => import("@/views/VehiclesView.vue"),
        meta: { title: "Vozila" },
    },
    {
        path: "/mileage",
        name: "mileage",
        component: () => import("@/views/MileageView.vue"),
        meta: { title: "Kilometrine" },
    },
    {
        // Payment batches for the bank (the classic placilniPrometV2_placila_*); the batch number is in the query string.
        path: "/payments",
        name: "payments",
        component: () => import("@/views/PaymentsView.vue"),
        meta: { title: "Plačila" },
    },
    {
        path: "/payment",
        name: "payment-edit",
        component: () => import("@/views/PaymentEditView.vue"),
        meta: { title: "Plačilo" },
    },
    {
        // Travel orders (the classic potniNalogi_seznamPotnihNalogovV2 / vnosPotnegaNalogaV2); the order number is in the query string.
        path: "/travel-orders",
        name: "travel-orders",
        component: () => import("@/views/TravelOrdersView.vue"),
        meta: { title: "Potni nalogi" },
    },
    {
        path: "/travel-order",
        name: "travel-order",
        component: () => import("@/views/TravelOrderEditView.vue"),
        meta: { title: "Potni nalog" },
    },
    {
        // General-ledger reports (the classic izpisi_brutobilancaV2 / izpisi_kontokarticeV2).
        path: "/trial-balance",
        name: "trial-balance",
        component: () => import("@/views/TrialBalanceView.vue"),
        meta: { title: "Bruto bilanca" },
    },
    {
        path: "/account-cards",
        name: "account-cards",
        component: () => import("@/views/AccountCardsView.vue"),
        meta: { title: "Kontokartice" },
    },
    {
        // Manual open-item ledger (the classic placilniPromet_rocniSK).
        path: "/manual-ledger",
        name: "manual-ledger",
        component: () => import("@/views/ManualLedgerView.vue"),
        meta: { title: "Ročni SK" },
    },
    {
        path: "/admin/companies",
        name: "admin-companies",
        component: () => import("@/views/AdminCompaniesView.vue"),
        meta: { title: "Podjetja" },
    },
    {
        path: "/admin/targets",
        name: "admin-targets",
        component: () => import("@/views/AdminTargetsView.vue"),
        meta: { title: "Ciljna skupina" },
    },
    {
        // First-time company data of a "Sam svoj računovodja" company, in three saved steps.
        path: "/podatki-o-podjetju",
        name: "company-setup",
        component: () => import("@/views/CompanySetupView.vue"),
        meta: { title: "Podatki o podjetju" },
    },
];

const router = createRouter({
    // Vite's vite.config.ts has base "/v2/app/" — Vue Router's history base
    // must match so links like to="/invoices" resolve to /v2/app/invoices.
    history: createWebHistory("/v2/app/"),
    routes,
});

// Pages that need a V2 screen right (HEAD_MENU row + PRAVICE_UPORABNIKOV, entered by hand).
const REQUIRED_RIGHT: Record<string, string> = {
    "dashboard":       "v2_adminPregledPf",
    "crm":             "v2_crm",
    "admin":           "v2_admin",
    "admin-companies": "v2_adminPodjetja",
    "admin-targets":   "v2_adminPodjetja",
};

// Screens reached through the menu: the user needs one of these menu entries.
const REQUIRED_MENU: Record<string, string[]> = {
    // The kinds of invoice share these screens; the API checks the right of the kind asked for.
    "invoices":       ALL_KIND_MENUS,
    "invoice-detail": ALL_KIND_MENUS,
    "invoice-edit":   ["racuni_izdaniRacuni", "knjizenje_izdaniRacuni", "racuni_prejetiRacuni", "knjizenje_prejetiRacuni", "racuni_izdaniRacuniIzMasovneObdelave", "racuni_gotovinskiRacuni", "knjizenjeV2_gotovinskiRacuni"],
    "invoice-new":    ["racuni_izdaniRacuni", "knjizenje_izdaniRacuni", "racuni_ponudbe_seznamIzdanihPonudb", "racuni_prejetiRacuni", "knjizenje_prejetiRacuni", "racuni_gotovinskiRacuni", "knjizenjeV2_gotovinskiRacuni"],
    "invoice-look":   ["racuni_izdaniRacuni", "knjizenje_izdaniRacuni"],
    "partners":       ["partnerji_seznamPartnerjev"],
    "partner-edit":  ["partnerji_seznamPartnerjev"],
    "partner-contacts":        ["partnerji_kontaktneOsebe"],
    "einvoice-subscribers":    ["partnerji_eRacuniNarocniki"],
    "item-groups":    ["sifranti_skupineMaterialBlagoStoritve"],
    "articles":       ["sifranti_materialBlagoStoritve2"],
    "clauses":        ["sifranti_klavzule"],
    "postal-codes":   ["sifranti_poste"],
    "vehicles":       ["potniNalogi_vozila", "sifranti_vozila"],
    "mileage":        ["potniNalogi_kilometrine", "sifranti_kilometrine"],
    "payments":       ["placilniPromet_placila", "placilniPrometV2_placila_seznamPlacil"],
    "payment-edit":   ["placilniPromet_placila", "placilniPrometV2_placila_seznamPlacil"],
    "travel-orders":  ["potniNalogi_seznamPotnihNalogov", "potniNalogi_seznamPotnihNalogovV2"],
    "travel-order":   ["potniNalogi_seznamPotnihNalogov", "potniNalogi_seznamPotnihNalogovV2"],
    "manual-ledger":  ["placilniPromet_rocniSK"],
    "trial-balance":  ["izpisi_brutobilancaV2"],
    "account-cards":  ["izpisi_kontokarticeV2"],
    "bilance-uvoz":   ["bilance_uvozSp", "v2_bilanceUvozSp"],
    "bilance-pregled": ["bilance_pregledV2", "v2_bilancePregled"],
};

// The only page open to every signed-in user: the home page.
const OPEN_TO_ALL = new Set(["financial-data", "company-setup", "settings"]);

// Nothing opens by its address unless it is among the user's rights. A route that is in none of the
// lists above is refused too, so a new screen has to be given a rule on purpose.
router.beforeEach(async (to) => {
    const menu = useMenuStore();
    await menu.load();
    if (menu.error) return true; // not logged in -> the 401 handler is already leaving for /v2/login

    const name = String(to.name);

    // A new "Sam svoj računovodja" company has to give its data first; nothing else is of use until then.
    // The screen is only for that: once the setup is done it has nothing left to ask.
    if (menu.setupNeeded && name !== "company-setup") return { path: "/podatki-o-podjetju", replace: true };
    if (!menu.setupNeeded && name === "company-setup") return { path: "/financial-data", replace: true };

    let allowed = OPEN_TO_ALL.has(name);
    if (REQUIRED_RIGHT[name]) allowed = menu.can(REQUIRED_RIGHT[name]);
    else if (REQUIRED_MENU[name]) allowed = menu.mayOpen(...REQUIRED_MENU[name]);
    else if (name === "legacy") allowed = menu.mayOpen(String(to.params.component));
    else if (name === "subscription") allowed = menu.hasSubscription;
    if ((name === "partners" || name === "partner-edit") && !menu.isSsr) allowed = false;

    return allowed ? true : { path: "/financial-data", replace: true };
});

router.afterEach((to) => {
    const base = "Epodjetnik";
    document.title = to.meta?.title ? `${to.meta.title} | ${base}` : base;
});

export default router;
